/**
 * Three-up feature row for the landing page. Each card surfaces one
 * pedagogically-evidenced differentiator plus a quick visual hint.
 * Card accent colours map to subject hues: Accountancy indigo-blue,
 * Business Studies terracotta, Economics forest.
 *
 * Khadi & Indigo: warm cream surface, hairline border, soft warm
 * shadows + hover lift, scroll-reveal entrance animation.
 */
import { useScrollReveal } from '@/lib/useScrollReveal';

export function FeatureCards() {
  return (
    <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5 md:gap-6">
      <AnimatedCard delay={0}>
        <FeatureCard
          icon={<ExampleIcon />}
          iconGradient="from-acct-100 to-acct-50"
          iconColor="text-acct-600"
          borderHover="hover:border-acct-500/40 hover:shadow-glow-acct"
          accentColor="#2f3a8f"
          accentLight="rgba(47,58,143,0.07)"
          title="Worked examples, one step at a time"
          body="Every chapter front-loads a fully solved problem with steps revealed one at a time — the approach shown to halve learning time in tutor research."
          tag="Evidence-backed"
          tagColor="text-acct-600"
          tagBg="bg-acct-50 border-acct-200"
        />
      </AnimatedCard>

      <AnimatedCard delay={80}>
        <FeatureCard
          icon={<RatioIcon />}
          iconGradient="from-biz-100 to-biz-50"
          iconColor="text-biz-500"
          borderHover="hover:border-biz-500/40 hover:shadow-glow-biz"
          accentColor="#b54a2b"
          accentLight="rgba(181,74,43,0.07)"
          title="Built for CBSE & Kerala — chapter by chapter"
          body="All 137 chapters aligned to your exact board syllabus — CBSE NCERT or Kerala SCERT. Indian examples throughout. No login, no fee."
          tag="137 chapters"
          tagColor="text-biz-600"
          tagBg="bg-biz-50 border-biz-200"
        />
      </AnimatedCard>

      <AnimatedCard delay={160}>
        <FeatureCard
          icon={<TutorIcon />}
          iconGradient="from-econ-100 to-econ-50"
          iconColor="text-econ-600"
          borderHover="hover:border-econ-500/40 hover:shadow-glow-econ"
          accentColor="#1f6f5c"
          accentLight="rgba(31,111,92,0.07)"
          title="A tutor that never spoils the answer"
          body="Stuck? Three escalating hints. Each one points at a rule or asks you to notice a contradiction. None of them tell you the answer — that's the whole point."
          tag="Guided hints"
          tagColor="text-econ-600"
          tagBg="bg-econ-50 border-econ-200"
        />
      </AnimatedCard>
    </div>
  );
}

// ── Animated wrapper ─────────────────────────────────────────────────────────

function AnimatedCard({ children, delay }: { children: React.ReactNode; delay: number }) {
  const ref = useScrollReveal<HTMLDivElement>({ delay, threshold: 0.08 });
  return <div ref={ref}>{children}</div>;
}

// ── Internal ─────────────────────────────────────────────────────────────────

interface CardProps {
  icon: React.ReactNode;
  iconGradient: string;
  iconColor: string;
  borderHover: string;
  accentColor: string;
  accentLight: string;
  title: string;
  body: string;
  tag: string;
  tagColor: string;
  tagBg: string;
}

function FeatureCard({
  icon, iconGradient, iconColor, borderHover,
  accentColor, accentLight,
  title, body, tag, tagColor, tagBg,
}: CardProps) {
  return (
    <div
      className={`
        group relative rounded-card-lg border border-ink-200/80 bg-cream overflow-hidden
        shadow-card
        transition-[transform,box-shadow,border-color] duration-200 ease-out-soft cursor-default
        hover:-translate-y-1 hover:shadow-card-raised motion-reduce:hover:translate-y-0
        ${borderHover}
      `}
      style={{ borderTop: `2.5px solid ${accentColor}` }}
    >
      {/* Subtle inner glow on hover */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none rounded-card-lg"
        style={{ background: `radial-gradient(ellipse at 50% 0%, ${accentLight} 0%, transparent 70%)` }}
      />

      <div className="relative p-6 md:p-7">
        {/* Icon */}
        <div
          className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 bg-gradient-to-br shadow-[inset_0_1px_0_rgba(255,255,255,0.8),0_1px_2px_rgba(27,31,59,0.06)] ${iconGradient} ${iconColor}`}
        >
          {icon}
        </div>

        {/* Tag chip */}
        <span className={`inline-flex items-center text-[10px] font-semibold uppercase tracking-[0.1em] px-2 py-0.5 rounded-full border mb-3 ${tagColor} ${tagBg}`}>
          {tag}
        </span>

        <h3 className="font-serif text-[1.18rem] text-ink-900 leading-snug tracking-tight mb-2.5 font-semibold text-balance">{title}</h3>
        <p className="text-ink-600 text-[15px] leading-relaxed">{body}</p>
      </div>
    </div>
  );
}

function ExampleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="w-5.5 h-5.5 w-[1.375rem] h-[1.375rem]">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M7 8h10M7 12h7M7 16h4" strokeLinecap="round" />
      <circle cx="18" cy="15" r="2" fill="currentColor" stroke="none" />
    </svg>
  );
}

function RatioIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="w-[1.375rem] h-[1.375rem]">
      <path d="M3 20h18M5 20V10m4 10V6m4 14v-8m4 8V8" strokeLinecap="round" />
    </svg>
  );
}

function TutorIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="w-[1.375rem] h-[1.375rem]">
      <path d="M4 5h12a3 3 0 013 3v6a3 3 0 01-3 3h-3l-4 3v-3H4a1 1 0 01-1-1V6a1 1 0 011-1z" strokeLinejoin="round" />
      <path d="M9 10h6M9 13h4" strokeLinecap="round" />
    </svg>
  );
}
